import React, { type ReactNode } from 'react';
import type { WidgetConfig } from '@/components/dashboard/types';
import { DreamspellSkin } from '@/components/skins/DreamspellSkin';
import type { CycleRing } from '@/lib/cycleTypes';

export interface Skin {
  id: string;
  name: string;
  description: string;
  component: React.ComponentType<SkinProps>;
  supportedCycles: string[]; // Widget IDs that this skin can bind to
  defaultCycleId?: string;
}

export interface SkinProps {
  cycleWidget: WidgetConfig | null;
  currentDate?: Date;
  cycleRings?: CycleRing[];
  hiddenRings?: string[];
  onToggleRing?: (ringId: string) => void;
  onRingsChange?: (next: CycleRing[]) => void;
  onRotationChange?: (dayOfYear: number, fractionOfDay: number) => void;
  onSyncToNow?: () => void;
  cycleDisplayMode?: 'single' | 'multi';
  onCycleDisplayModeChange?: (mode: 'single' | 'multi') => void;
  navItems?: Array<{ id: string; label: string; icon: ReactNode; to: string }>;
  onOpenTheme?: () => void;
  isAdmin?: boolean;
  viewMode?: 'skin' | 'pager' | 'grid';
  onViewModeChange?: (mode: 'skin' | 'pager' | 'grid') => void;
  widgets?: WidgetConfig[];
  hiddenWidgets?: WidgetConfig[];
  addableWidgets?: WidgetConfig[];
  onAddWidgetById?: (widgetId: string) => void;
  selectedCycleWidgetId?: string;
  onSelectCycleWidget?: (widgetId: string) => void;
  onToggleWidgetVisibility?: (widgetId: string) => void;
  onToggleWidgetSync?: (widgetId: string) => void;
  widgetSyncModes?: Record<string, 'linked' | 'isolated' | 'fixed'>;
  widgetCustomizations?: Record<string, { slideRow?: 0 | 1 | 2; syncMode?: 'linked' | 'isolated' | 'fixed'; fixedTimestamp?: string; title?: string; selectedCycleWidgetId?: string; cycleRailHidden?: boolean; dreamspellDefaultRow?: 'short' | 'medium' | 'tall'; dreamspellRowDefaults?: Partial<Record<'short' | 'medium' | 'tall', string>>; showInShort?: boolean; showInMedium?: boolean; showInTall?: boolean; pagerGridVariant?: 'auto' | 'tall' | 'short' }>;
  onWidgetCustomizationChange?: (widgetId: string, customizationPatch: { slideRow?: 0 | 1 | 2; dreamspellDefaultRow?: 'short' | 'medium' | 'tall' | null; dreamspellRowDefaults?: Partial<Record<'short' | 'medium' | 'tall', string>> | null; selectedCycleWidgetId?: string; cycleRailHidden?: boolean; showInShort?: boolean; showInMedium?: boolean; showInTall?: boolean; tallRowVisible?: boolean }) => void;
  skinRowOrder?: ('short' | 'medium' | 'tall')[];
  onSkinRowOrderChange?: (order: ('short' | 'medium' | 'tall')[]) => void;
  skinRowFractions?: [number, number, number];
  onSkinRowFractionsChange?: (fractions: [number, number, number]) => void;
  onSkinRowFractionsCommit?: (fractions: [number, number, number]) => void;
  isNowLocked?: boolean;
}

export const SKINS: Skin[] = [
  {
    id: 'dreamspell',
    name: '13 Cycle Dreamspell',
    description: 'Mystical astronomical calendar view with interlocking cycles',
    component: DreamspellSkin,
    supportedCycles: ['grid', 'gregorian-grid', 'cycles'],
    defaultCycleId: 'gregorian-grid',
  },
  // Future skins will be added here
];

export function getSkinById(skinId: string): Skin | undefined {
  return SKINS.find(skin => skin.id === skinId);
}

export function getDefaultSkin(): Skin {
  return SKINS[0];
}
